'use client';

import { useTranslations } from 'next-intl';
import { BookTestCta, ComingSoonEyebrow } from './BookTestCta';
import { useBloodVisionBooking } from './hooks/useBloodVisionBooking';
import { Container } from '..';
import { H2, PBody as P } from './styleConstants';
import Image from 'next/image';
import styles from './TestsShowcaseSection.module.css';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useRef } from 'react';
import gsap from 'gsap';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useSearchParams } from 'next/navigation';
import { getAssetUrl } from 'lib/config/assetUrls';

type TestDataType = {
  icon: string;
  panel: string;
  tag: 'male' | 'female' | 'all';
  parameterCount: number;
};

const TestCardsData: Array<Array<TestDataType>> = [
  [
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/08.svg'),
      panel: 'peakPerformance',
      tag: 'male',
      parameterCount: 104,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/01.svg'),
      panel: 'advanced',
      tag: 'male',
      parameterCount: 93,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/02.svg'),
      panel: 'foundational',
      tag: 'male',
      parameterCount: 72,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/02.svg'),
      panel: 'peakPerformance',
      tag: 'female',
      parameterCount: 102,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/04.svg'),
      panel: 'advanced',
      tag: 'female',
      parameterCount: 95,
    },
  ],
  [
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/05.svg'),
      panel: 'foundational',
      tag: 'female',
      parameterCount: 74,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/06.svg'),
      panel: 'heartHealth',
      tag: 'all',
      parameterCount: 10,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/07.svg'),
      panel: 'diabetesRisk',
      tag: 'all',
      parameterCount: 9,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/11.svg'),
      panel: 'foundational',
      tag: 'male',
      parameterCount: 72,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/13.svg'),
      panel: 'advanced',
      tag: 'male',
      parameterCount: 93,
    },
  ],
  [
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/09.svg'),
      panel: 'inflammationStatus',
      tag: 'all',
      parameterCount: 31,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/10.svg'),
      panel: 'sleepStatus',
      tag: 'all',
      parameterCount: 35,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/12.svg'),
      panel: 'fatigue',
      tag: 'all',
      parameterCount: 46,
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/01.svg'),
      panel: 'peakPerformance',
      tag: 'male',
      parameterCount: 104,
    },
  ],
];

const TestCards = ({ icon, panel, tag, parameterCount }: TestDataType) => {
  const t = useTranslations('bloodVision');
  const title = t(`panels.${panel}.title`);

  return (
    <div className={styles.card}>
      <Image src={icon} alt={title} width={48} height={48} />
      <p className={styles.cardTag}>
        {t(`panelTag.${tag}`, { count: parameterCount })}
      </p>
      <h5 className={styles.cardTitle}>{title}</h5>
      <p className={styles.cardDescription}>
        {t(`showcase.cards.${panel}`)}
      </p>
    </div>
  );
};

export const TestsShowcaseSection = ({
  className,
}: {
  className?: string;
}) => {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const t = useTranslations('bloodVision');

    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const gsapContext = useGsapContext();

    const { width } = useWindowSize();

    const searchParams = useSearchParams();
    const booking = useBloodVisionBooking();

    useIsomorphicLayoutEffect(() => {
      if (!rootRef.current || !containerRef.current) return;

      intersectionObserver.current = new IntersectionObserver(
        () => {
          gsapContext.add(() => {
            const tl = gsap.timeline({
              defaults: {
                duration: 1,
              },
              scrollTrigger: {
                immediateRender: false,
                trigger: containerRef.current,
                start: 'top 90%',
                end: 'bottom top',
                scrub: 0,
                invalidateOnRefresh: true,
              },
            });

            tl.fromTo(
              '.col.even',
              { y: 0 },
              { y: width > 768 ? '-80%' : '-30%' },
            );
            tl.fromTo(
              '.col.odd',
              { y: 0 },
              { y: width > 768 ? '100%' : '30%' },
              0,
            );
          });
        },
        {
          root: null,
          rootMargin: '20px',
          threshold: 0.01,
        },
      );

      intersectionObserver.current.observe(rootRef.current);

      return () => {
        intersectionObserver.current?.disconnect();
        // gsapContext.clear();
      };
    }, []);

    return (
      <section
        className={className ? `${styles.root} ${className}` : styles.root}
        ref={rootRef}
      >
        <Container className={styles.content}>
          <ComingSoonEyebrow />
          <H2 className={styles.title}>{t('showcase.title')}</H2>
          <P className={styles.subtitle}>{t('showcase.description')}</P>
        </Container>
        {booking ? (
          <div className={styles.ctaContainer}>
            <BookTestCta
              href={{
                pathname: '/blood-vision/buy',
                query: Object.fromEntries(searchParams?.entries() ?? []),
              }}
              className="primary"
            />
          </div>
        ) : null}
        <div className={styles.testListContainer} ref={containerRef}>
          {TestCardsData.map((element, index) => {
            return (
              <div
                key={index}
                className={`col ${(index + 1) % 2 === 0 ? 'even' : 'odd'}`}
              >
                {element.map((childElement, childIndex) => {
                  return <TestCards key={childIndex} {...childElement} />;
                })}
              </div>
            );
          })}
        </div>

        <div className={styles.locationDisclaimer}>
          {/* *We&apos;re currently available in <b>India</b> across{' '}
          <b>Bangalore,</b> <b>Mumbai,</b> <b>Delhi,</b> <b>Hyderabad,</b>{' '}
          <b>Noida,</b> <b>Gurugram</b> and <b>Faridabad</b>. */}
          {t.rich('showcase.availability', {
            b: (chunks) => <b>{chunks}</b>,
            br: () => <br />,
          })}
        </div>
      </section>
    );
};
